import {
  BMComponentsType,
  BMPrimitives,
  useBMTheme,
} from "@traveloka/district-bloom";
import IcSystemCrossClose from "@traveloka/icon-kit-web/react/IcSystemCrossClose";
import { useCallback, useMemo, useState } from "react";
import {
  ImageBackground,
  LayoutChangeEvent,
  Pressable,
  StyleSheet,
} from "react-native";
import { BANNER_WARNING_CONE_BASE64 } from "../../assets/banner";
import SimpleMarkdown from "../SimpleMarkdown";
import BMView from "../View/BMView";
import { DEFAULT_BANNER_HEIGHT, DESKTOP_BANNER_HEIGHT } from "./constant";

export type BannerProps = {
  id: string | number;
  type: "WARNING" | "INFO";
  content: string;
  onClose?: (id: string | number) => void;
};

export default function Banner({ id, type, content, onClose }: BannerProps) {
  const { BMComponents } = useBMTheme();
  const [containerWidth, setContainerWidth] = useState<number>(0);

  const styles = useMemo(() => createStyles(BMComponents), [BMComponents]);
  const isDesktopView = useMemo(() => containerWidth >= 640, [containerWidth]);

  const handleLayout = useCallback((event: LayoutChangeEvent) => {
    setContainerWidth(event.nativeEvent.layout.width);
  }, []);

  return (
    <BMView
      row
      justify="center"
      align="center"
      style={[
        {
          height: isDesktopView ? DESKTOP_BANNER_HEIGHT : DEFAULT_BANNER_HEIGHT,
        },
        type === "WARNING" ? styles.warningContainer : styles.infoContainer,
      ]}
      onLayout={handleLayout}
    >
      {type === "WARNING" ? (
        <ImageBackground
          style={[
            styles.illustrationContainer,
            isDesktopView
              ? styles.illustrationContainerDesktop
              : styles.illustrationContainerMobile,
          ]}
          imageStyle={[
            styles.illustration,
            isDesktopView
              ? styles.illustrationDesktop
              : styles.illustrationMobile,
          ]}
          source={{ uri: BANNER_WARNING_CONE_BASE64 }}
          alt={"Warning Cone Illustration for Banner"}
        />
      ) : null}
      <BMView flex style={styles.descriptionContainer}>
        <SimpleMarkdown
          numberOfLines={2}
          markdown={content}
          variant="uiLargeLi"
          ellipsizeMode="tail"
          style={
            isDesktopView ? styles.descriptionDesktop : styles.descriptionMobile
          }
        />
      </BMView>
      <Pressable onPress={() => onClose?.(id)}>
        <BMView style={styles.closeButtonContainer}>
          <IcSystemCrossClose
            color={BMComponents.BMStyleLineBordercolorActive}
          />
        </BMView>
      </Pressable>
    </BMView>
  );
}

function createStyles(BMComponents: BMComponentsType) {
  return StyleSheet.create({
    warningContainer: {
      /**
       * Background color not registered in BMPrimitives
       * https://www.figma.com/design/76V34qYtiWtiiLzivdGxXU/2025_03_-CTV--Platform-Communication?node-id=4533-26781&t=HG9pWCi3hWBRNEDj-0
       */
      backgroundColor: "#FEE050",
    },
    infoContainer: {
      paddingLeft: BMPrimitives.BMSize.md,
      backgroundColor: BMComponents.BMStyleSurfaceBgcolor,
    },
    illustrationContainer: {
      height: "100%",
      overflow: "hidden",
    },
    illustrationContainerDesktop: {
      width: 88,
    },
    illustrationContainerMobile: {
      width: 40,
    },
    illustration: {
      resizeMode: "contain",
    },
    illustrationDesktop: {
      width: "100%",
      height: "125%",
    },
    illustrationMobile: {
      width: "190%",
      height: "100%",
      left: -33,
    },
    descriptionContainer: {
      margin: BMPrimitives.BMSize.md,
    },
    descriptionDesktop: {
      textAlign: "center",
    },
    descriptionMobile: {
      textAlign: "left",
    },
    closeButtonContainer: {
      marginRight: BMPrimitives.BMSize.md,
    },
  });
}
